import axios from "axios";
import type { BacpacSourceEnvironment, SqllyLaunch, SqllyPackage, SqllyRefreshResult, SqllyStatus } from "@/lib/types";

const api = axios.create({
  baseURL: "/api",
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register") {
        const callback = window.location.pathname + window.location.search + window.location.hash;
        window.location.href = `/login?callbackUrl=${encodeURIComponent(callback)}`;
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/** The message to show for a failed call: the ProblemDetails `detail`, else its `title`, else `fallback`. */
export function problemDetail(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { detail?: string; title?: string } } })?.response?.data;
  return data?.detail ?? data?.title ?? fallback;
}

// Server pool API functions
export const serversApi = {
  list: () => api.get("/servers").then((r) => r.data),
  get: (id: number) => api.get(`/servers/${id}`).then((r) => r.data),
  create: (data: import("./types").CreateServerRequest) =>
    api.post("/servers", data).then((r) => r.data),
  update: (id: number, data: import("./types").UpdateServerRequest) =>
    api.put(`/servers/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/servers/${id}`),
  testConnection: (id: number) =>
    api.post(`/servers/${id}/test`).then((r) => r.data),
  refreshHealth: (id: number) =>
    api.post(`/servers/${id}/refresh`).then((r) => r.data),
  setEnabled: (id: number, enabled: boolean) =>
    api.put(`/servers/${id}/enabled`, { enabled }),
  getContainers: (id: number) =>
    api.get(`/servers/${id}/containers`).then((r) => r.data),
  containerAction: (id: number, containerId: string, action: string) =>
    api.post(`/servers/${id}/containers/${encodeURIComponent(containerId)}/${action}`),
  branchContainersAction: (id: number, branchId: number, action: "start" | "stop" | "restart") =>
    api.post<{ count: number }>(`/servers/${id}/branches/${branchId}/containers/${action}`).then((r) => r.data),
  removeContainer: (id: number, containerId: string) =>
    api.delete(`/servers/${id}/containers/${encodeURIComponent(containerId)}`),
  getContainerLogs: (id: number, containerId: string, tail = 200) =>
    api.get(`/servers/${id}/containers/${encodeURIComponent(containerId)}/logs`, { params: { tail } }).then((r) => r.data),
};

// DNS zone management
export const dnsApi = {
  listZones: () => api.get("/dns/zones").then((r) => r.data),
  getZone: (id: number) => api.get(`/dns/zones/${id}`).then((r) => r.data),
  createZone: (data: import("./types").CreateDnsZoneRequest) =>
    api.post("/dns/zones", data).then((r) => r.data),
  updateZone: (id: number, data: import("./types").UpdateDnsZoneRequest) =>
    api.put(`/dns/zones/${id}`, data).then((r) => r.data),
  deleteZone: (id: number) => api.delete(`/dns/zones/${id}`),
  testZone: (id: number) =>
    api.post(`/dns/zones/${id}/test`).then((r) => r.data),
  getBranchRecords: (branchId: number) =>
    api.get(`/branches/${branchId}/dns`).then((r) => r.data),
  provisionBranch: (branchId: number) =>
    api.post(`/branches/${branchId}/dns/provision`).then((r) => r.data),
  deprovisionBranch: (branchId: number) =>
    api.delete(`/branches/${branchId}/dns`).then((r) => r.data),
  getProvisionEnabled: () =>
    api.get("/dns/provision-enabled").then((r) => r.data as { provisionEnabled: boolean }),
};

// Project management
export const projectsApi = {
  list: () => api.get("/projects").then((r) => r.data),
  get: (id: number) => api.get(`/projects/${id}`).then((r) => r.data),
  create: (data: {
    name: string;
    platform: string;
    organizationUrl?: string;
    projectIdentifier?: string;
    credential?: string;
    credentialId?: string;
    isDefault: boolean;
  }) => api.post("/projects", data).then((r) => r.data),
  update: (id: number, data: {
    name?: string;
    organizationUrl?: string;
    projectIdentifier?: string;
    credential?: string;
    credentialId?: string;
    isDefault?: boolean;
  }) => api.put(`/projects/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/projects/${id}`),
  addRepository: (projectId: number, data: { name: string; cloneUrl: string; isDefault: boolean }) =>
    api.post(`/projects/${projectId}/repositories`, data).then((r) => r.data),
  updateRepository: (projectId: number, repoId: number, data: { name: string; cloneUrl: string; isDefault: boolean }) =>
    api.put(`/projects/${projectId}/repositories/${repoId}`, data).then((r) => r.data),
  deleteRepository: (projectId: number, repoId: number) =>
    api.delete(`/projects/${projectId}/repositories/${repoId}`),
};

// System credentials (legacy)
export const systemCredentialsApi = {
  getAll: () => api.get("/system/credentials").then((r) => r.data),
  save: (type: string, data: { value?: string; displayName?: string; defaultModel?: string }) =>
    api.put(`/system/credentials/${type}`, data),
  delete: (type: string) => api.delete(`/system/credentials/${type}`),
};

// Unified credentials
export const credentialsApi = {
  list: (scope: string) =>
    api.get("/credentials", { params: { scope } }).then((r) => r.data),
  get: (id: number) => api.get(`/credentials/${id}`).then((r) => r.data),
  createSystem: (data: {
    credentialType: string;
    displayName: string;
    value: string;
    secondaryValue?: string;
    metadata?: string;
    isDefault?: boolean;
  }) => api.post("/credentials/system", data).then((r) => r.data),
  createUser: (data: {
    credentialType: string;
    displayName: string;
    value: string;
    secondaryValue?: string;
    metadata?: string;
    isDefault?: boolean;
  }) => api.post("/credentials/user", data).then((r) => r.data),
  update: (
    id: number,
    data: {
      displayName?: string;
      value?: string;
      secondaryValue?: string;
      metadata?: string;
      isDefault?: boolean;
    }
  ) => api.put(`/credentials/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/credentials/${id}`),
  validate: (id: number) =>
    api.post(`/credentials/${id}/validate`).then((r) => r.data),
  servicePrincipal: (id: number) =>
    api.get(`/credentials/${id}/service-principal`).then((r) => r.data),
  testLogin: (id: number) =>
    api.post(`/credentials/${id}/test-login`).then((r) => r.data),
  setDefault: (id: number) =>
    api.post(`/credentials/${id}/set-default`).then((r) => r.data),
  resolve: (params: {
    type: string;
    branchId?: number;
    projectId?: number;
  }) => api.get("/credentials/resolve", { params }).then((r) => r.data),
  getAssignments: (targetType: string, targetId: number) =>
    api
      .get("/credential-assignments", { params: { targetType, targetId } })
      .then((r) => r.data),
  assign: (
    targetType: string,
    targetId: number,
    data: { credentialId: number; purpose?: string }
  ) =>
    api
      .post(`/credential-assignments/${targetType}/${targetId}`, data)
      .then((r) => r.data),
  unassign: (id: number) => api.delete(`/credential-assignments/${id}`),
  aiModels: () => api.get("/credentials/ai-models").then((r) => r.data),
};

export const branchResourcesApi = {
  updateResources: (id: number, data: { cpuCores?: number; memoryMb?: number; diskGb?: number }) =>
    api.put(`/branches/${id}/resources`, data),
  migrate: (id: number, targetServerId: number) =>
    api.post(`/branches/${id}/migrate`, { targetServerId }),
};

export const branchesApi = {
  list: () => api.get("/branches").then((r) => r.data),
  get: (id: number) => api.get(`/branches/${id}`).then((r) => r.data),
};

// Production/dev BACPAC refresh + per-environment export cache
export const branchBackupsApi = {
  list: (branchId: number) =>
    api.get(`/branches/${branchId}/backups`).then((r) => r.data),
  create: (branchId: number, data: { app: boolean; auth: boolean }) =>
    api.post(`/branches/${branchId}/backups`, data).then((r) => r.data),
  setKeep: (branchId: number, name: string, keep: boolean) =>
    api.put(`/branches/${branchId}/backups/${encodeURIComponent(name)}/keep`, { keep }),
  delete: (branchId: number, name: string) =>
    api.delete(`/branches/${branchId}/backups/${encodeURIComponent(name)}`),
  /**
   * Uploads a BACPAC as the raw request body and queues its restore over the App or Auth
   * database, whichever the server finds it is a copy of. Sent as octet-stream so the proxy
   * streams it instead of buffering it.
   */
  restore: (branchId: number, file: File, onProgress?: (fraction: number) => void) =>
    api
      .post(`/branches/${branchId}/backups/restore`, file, {
        params: { fileName: file.name },
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => e.total && onProgress?.(e.loaded / e.total),
      })
      .then((r) => r.data),
  /** Plain GET through the proxy — used as an <a href> so the browser streams the file to disk. */
  downloadUrl: (branchId: number, name: string, role: string) =>
    `/api/branches/${branchId}/backups/${encodeURIComponent(name)}/${role}`,
};

export const prodBacpacApi = {
  status: (branchId: number) =>
    api.get(`/branches/${branchId}/prod-bacpac`).then((r) => r.data),
  refresh: (
    branchId: number,
    data: {
      useLive: boolean;
      folderName?: string | null;
      source: BacpacSourceEnvironment;
    }
  ) => api.post(`/branches/${branchId}/prod-bacpac`, data).then((r) => r.data),
  deleteCached: (
    branchId: number,
    folderName: string,
    source: BacpacSourceEnvironment
  ) =>
    api.delete(`/branches/${branchId}/prod-bacpac/cache`, {
      params: { folderName, source },
    }),
};

// SQL project (.sqlproj) publishes against the branch's App database
export const sqlProjectApi = {
  apply: (branchId: number, allowDataLoss: boolean) =>
    api.post(`/branches/${branchId}/sql-project/apply`, { allowDataLoss }).then((r) => r.data),
  createNew: (branchId: number) =>
    api.post(`/branches/${branchId}/sql-project/create`).then((r) => r.data),
};

// Deployment profiles
export const profilesApi = {
  list: () => api.get("/deployment-profiles").then((r) => r.data),
  get: (id: number) => api.get(`/deployment-profiles/${id}`).then((r) => r.data),
  create: (data: { name: string; description?: string; isDefault: boolean; parameterMode: string; queryFailureMode: string }) =>
    api.post("/deployment-profiles", data).then((r) => r.data),
  update: (id: number, data: { name: string; description?: string; isDefault: boolean; parameterMode: string; queryFailureMode: string }) =>
    api.put(`/deployment-profiles/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/deployment-profiles/${id}`),
  clone: (id: number) => api.post(`/deployment-profiles/${id}/clone`).then((r) => r.data),
  addQuery: (id: number, data: { savedQueryId: number; executionOrder: number; allowDml: boolean; isEnabled: boolean }) =>
    api.post(`/deployment-profiles/${id}/queries`, data).then((r) => r.data),
  updateQuery: (id: number, dpqId: number, data: { executionOrder: number; allowDml: boolean; isEnabled: boolean }) =>
    api.put(`/deployment-profiles/${id}/queries/${dpqId}`, data).then((r) => r.data),
  removeQuery: (id: number, dpqId: number) =>
    api.delete(`/deployment-profiles/${id}/queries/${dpqId}`),
  reorderQueries: (id: number, items: { id: number; executionOrder: number }[]) =>
    api.put(`/deployment-profiles/${id}/queries/reorder`, { items }),
  setParameters: (id: number, parameterIds: number[]) =>
    api.put(`/deployment-profiles/${id}/parameters`, { parameterIds }),
  assignToBranch: (branchId: number, profileId: number | null) =>
    api.put(`/branches/${branchId}/profile`, { deploymentProfileId: profileId }),
  runBranchQueries: (branchId: number) =>
    api.post(`/branches/${branchId}/run-profile-queries`),
};

// Greenfield integrations/workflows/tasks APIs
export const integrationsApi = {
  list: () => api.get("/integrations").then((r) => r.data),
  get: (id: number) => api.get(`/integrations/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post("/integrations", data).then((r) => r.data),
  update: (id: number, data: Record<string, unknown>) => api.put(`/integrations/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/integrations/${id}`),
};

export const workflowsApi = {
  list: () => api.get("/workflows").then((r) => r.data),
  get: (id: number) => api.get(`/workflows/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post("/workflows", data).then((r) => r.data),
  update: (id: number, data: Record<string, unknown>) => api.put(`/workflows/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/workflows/${id}`),
};

export const workflowRunsApi = {
  list: (params?: { workflowId?: number; taskItemId?: number; status?: string }) =>
    api.get("/workflow-runs", { params }).then((r) => r.data),
  get: (id: number) => api.get(`/workflow-runs/${id}`).then((r) => r.data),
  start: (id: number) => api.post(`/workflow-runs/${id}/start`).then((r) => r.data),
};

export const testRunsApi = {
  list: () => api.get("/test-runs").then((r) => r.data),
  get: (id: number) => api.get(`/test-runs/${id}`).then((r) => r.data),
};

export const tasksApi = {
  list: () => api.get("/tasks").then((r) => r.data),
  get: (id: number) => api.get(`/tasks/${id}`).then((r) => r.data),
  events: (id: number) => api.get(`/tasks/${id}/events`).then((r) => r.data),
  runs: (taskItemId: number) => workflowRunsApi.list({ taskItemId }),
  routingRules: () => api.get("/tasks/routing-rules").then((r) => r.data),
  createRoutingRule: (data: Record<string, unknown>) => api.post("/tasks/routing-rules", data).then((r) => r.data),
  updateRoutingRule: (id: number, data: Record<string, unknown>) => api.put(`/tasks/routing-rules/${id}`, data).then((r) => r.data),
};

export const releasesApi = {
  list: () => api.get("/releases").then((r) => r.data),
  get: (id: string) => api.get(`/releases/${id}`).then((r) => r.data),
};

export const environmentsApi = {
  list: (params?: { releaseId?: string }) =>
    api.get("/environments", { params }).then((r) => r.data),
  get: (id: string) => api.get(`/environments/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post("/environments", data).then((r) => r.data),
  promote: (id: string, klass: string) => api.post(`/environments/${id}/promote`, { class: klass }).then((r) => r.data),
  archive: (id: string) => api.post(`/environments/${id}/archive`).then((r) => r.data),
};

export const targetPoolsApi = {
  list: () => api.get("/target-pools").then((r) => r.data),
  get: (id: string) => api.get(`/target-pools/${id}`).then((r) => r.data),
  create: (data: Record<string, unknown>) => api.post("/target-pools", data).then((r) => r.data),
  remove: (id: string) => api.delete(`/target-pools/${id}`).then((r) => r.data),
};

export const targetNodesApi = {
  list: (poolId?: string) => api.get("/target-nodes", { params: poolId ? { poolId } : undefined }).then((r) => r.data),
};

export const routesApi = {
  byEnvironment: (envId: string) => api.get(`/routes/by-environment/${envId}`).then((r) => r.data),
  apply: (data: Record<string, unknown>) => api.post("/routes", data).then((r) => r.data),
  remove: (id: string) => api.delete(`/routes/${id}`).then((r) => r.data),
};

export const resourcesApi = {
  classes: () => api.get("/resources/classes").then((r) => r.data),
  byEnvironment: (envId: string) => api.get(`/resources/by-environment/${envId}`).then((r) => r.data),
  bind: (data: Record<string, unknown>) => api.post("/resources", data).then((r) => r.data),
  remove: (envId: string, bindingId: string) => api.delete(`/resources/${envId}/${bindingId}`).then((r) => r.data),
};

export const buildDefaultsApi = {
  get: () => api.get("/build-defaults").then((r) => r.data),
  runtime: () => api.get("/build-defaults/runtime").then((r) => r.data),
};

export const detectionApi = {
  scan: (repoRoot: string, commitSha: string) =>
    api.post("/detection/scan", { repoRoot, commitSha }).then((r) => r.data),
};

export const generatedArtifactsApi = {
  byRelease: (releaseId: string) =>
    api.get(`/generated-artifacts/by-release/${releaseId}`).then((r) => r.data),
};

// AI workspace APIs
export const workspacesApi = {
  list: () => api.get("/workspaces").then((r) => r.data),
  get: (id: string) => api.get(`/workspaces/${id}`).then((r) => r.data),
  create: (data: { name: string; projectId?: number | null; branchId?: number | null; checkoutMode?: import("./types").WorkspaceCheckoutMode }) =>
    api.post("/workspaces", data).then((r) => r.data),
};

export const chatsApi = {
  list: () => api.get("/conversations").then((r) => r.data),
  listForWorkspace: (workspaceId: string) => api.get(`/workspaces/${workspaceId}/conversations`).then((r) => r.data),
  create: (workspaceId: string, data: { title: string }) => api.post(`/workspaces/${workspaceId}/conversations`, data).then((r) => r.data),
  messages: (conversationId: string) => api.get(`/conversations/${conversationId}/messages`).then((r) => r.data),
  send: (conversationId: string, data: { text: string; role?: import("./types").ConversationRole }) =>
    api.post(`/conversations/${conversationId}/messages`, data).then((r) => r.data),
};

export const agentRunsApi = {
  list: (params?: { workspaceId?: string; conversationId?: string }) => api.get("/agent-runs", { params }).then((r) => r.data),
  get: (runId: string) => api.get(`/agent-runs/${runId}`).then((r) => r.data),
  create: (data: {
    workspaceId: string;
    conversationId: string;
    runtimeKind: import("./types").AiRuntimeKind;
    checkoutMode?: import("./types").WorkspaceCheckoutMode;
    prompt?: string;
    requireApproval?: boolean;
    approvalReason?: string | null;
    runtimeProfileId?: string | null;
    providerProfileId?: string | null;
  }) =>
    api.post("/agent-runs", data).then((r) => r.data),
  events: (runId: string) => api.get(`/agent-runs/${runId}/events`).then((r) => r.data),
  jobs: (runId: string) => api.get(`/agent-runs/${runId}/jobs`).then((r) => r.data),
  cancel: (runId: string) => api.post(`/agent-runs/${runId}/cancel`).then((r) => r.data),
  send: (runId: string, message: string) => api.post(`/agent-runs/${runId}/messages`, { message }).then((r) => r.data),
};

export const approvalsApi = {
  pending: () => api.get("/approvals/pending").then((r) => r.data),
  resolve: (id: string, status: import("./types").ApprovalStatus) => api.post(`/approvals/${id}/resolve`, { status }).then((r) => r.data),
};

export const runtimeProfilesApi = {
  list: () => api.get("/runtime-profiles").then((r) => r.data),
  create: (data: { name: string; runtimeKind: import("./types").AiRuntimeKind; configJson?: string; isDefault?: boolean }) =>
    api.post("/runtime-profiles", data).then((r) => r.data),
};

export const providerProfilesApi = {
  list: () => api.get("/provider-profiles").then((r) => r.data),
  create: (data: { name: string; providerKind: import("./types").ModelProviderKind; defaultModel?: string; apiKeySecretReferenceId?: string | null; configJson?: string; isDefault?: boolean }) =>
    api.post("/provider-profiles", data).then((r) => r.data),
};

export const channelConnectionsApi = {
  list: () => api.get("/channels").then((r) => r.data),
  create: (data: { name: string; providerKind: import("./types").ChannelProviderKind; scope?: string; scopeId?: string | null; configJson?: string; secretRefsJson?: string | null; isEnabled?: boolean }) =>
    api.post("/channels", data).then((r) => r.data),
};

export const policiesApi = {
  list: () => api.get("/policies").then((r) => r.data),
  defaults: () => api.get("/policies/defaults").then((r) => r.data),
  create: (data: { name: string; policyJson?: string; isDefault?: boolean }) => api.post("/policies", data).then((r) => r.data),
};

export const secretsApi = {
  providers: () => api.get("/secrets/providers").then((r) => r.data),
  createProvider: (data: { name: string; providerKind: import("./types").SecretProviderKind; configJson?: string; isEnabled?: boolean }) =>
    api.post("/secrets/providers", data).then((r) => r.data),
  references: () => api.get("/secrets/references").then((r) => r.data),
  createReference: (data: { name: string; secretProviderConnectionId?: string | null; reference: string; defaultInjectionMode?: import("./types").SecretInjectionMode; metadataJson?: string | null }) =>
    api.post("/secrets/references", data).then((r) => r.data),
};

// Automations
export const automationsApi = {
  list: () => api.get("/automations").then((r) => r.data as import("./types").AutomationListItem[]),
  get: (id: number) => api.get(`/automations/${id}`).then((r) => r.data as import("./types").Automation),
  create: (data: { name: string; description?: string | null }) =>
    api.post("/automations", data).then((r) => r.data as import("./types").Automation),
  update: (id: number, data: { name: string; description?: string | null }) =>
    api.put(`/automations/${id}`, data).then((r) => r.data as import("./types").Automation),
  toggle: (id: number, enabled: boolean) =>
    api.post(`/automations/${id}/toggle?enabled=${enabled}`).then((r) => r.data),
  delete: (id: number) => api.delete(`/automations/${id}`),
  execute: (id: number, sourceBranchId?: number, ignoreFirstDeploy?: boolean) => {
    const qs = new URLSearchParams();
    if (sourceBranchId) qs.set("sourceBranchId", String(sourceBranchId));
    if (ignoreFirstDeploy) qs.set("ignoreFirstDeploy", "true");
    const q = qs.toString();
    return api.post(`/automations/${id}/execute${q ? "?" + q : ""}`).then((r) => r.data);
  },

  // Triggers (own + nested actions via UpsertTriggerInput)
  listTriggers: () => api.get("/automations/triggers").then((r) => r.data),
  getTrigger: (id: number) => api.get(`/automations/triggers/${id}`).then((r) => r.data),
  createTrigger: (data: import("./types").UpsertTriggerInput) =>
    api.post("/automations/triggers", data).then((r) => r.data),
  updateTrigger: (id: number, data: import("./types").UpsertTriggerInput) =>
    api.put(`/automations/triggers/${id}`, data).then((r) => r.data),
  toggleTrigger: (id: number, enabled: boolean) =>
    api.post(`/automations/triggers/${id}/toggle?enabled=${enabled}`),
  deleteTrigger: (id: number) => api.delete(`/automations/triggers/${id}`),

  // Runs
  listRuns: (params: { status?: string; triggerType?: string; from?: string; to?: string } = {}) =>
    api.get("/automations/runs", { params }).then((r) => r.data as import("./types").AutomationRunListItem[]),
  getRun: (id: number) => api.get(`/automations/runs/${id}`).then((r) => r.data as import("./types").AutomationRun),
};

export const automationAssetsApi = {
  list: () => api.get("/automation-assets").then((r) => r.data as import("./types").AutomationAsset[]),
  get: (id: number) => api.get(`/automation-assets/${id}`).then((r) => r.data as import("./types").AutomationAssetDetail),
  create: (data: { name: string; description?: string | null; contentType?: string; body: string; tags?: string | null }) =>
    api.post("/automation-assets", data).then((r) => r.data as import("./types").AutomationAsset),
  update: (id: number, data: { name: string; description?: string | null; contentType?: string; body?: string | null; tags?: string | null }) =>
    api.put(`/automation-assets/${id}`, data).then((r) => r.data as import("./types").AutomationAsset),
  delete: (id: number) => api.delete(`/automation-assets/${id}`),
  uploadBinary: (file: File, name?: string, description?: string, tags?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    if (description) fd.append("description", description);
    if (tags) fd.append("tags", tags);
    return api.post("/automation-assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
};

export const automationConnectionsApi = {
  list: () => api.get("/automation-connections").then((r) => r.data as import("./types").AutomationConnectionListItem[]),
  get: (id: number) => api.get(`/automation-connections/${id}`).then((r) => r.data as import("./types").AutomationConnectionDetail),
  create: (data: {
    name: string;
    direction: "Inbound" | "Outbound";
    targetUrl?: string | null;
    authHeadersJson?: string | null;
    httpMethod?: string | null;
    connectionType?: "Static" | "BranchLogin" | "SharedSecret";
    username?: string | null;
    password?: string | null;
    loginPathTemplate?: string | null;
    tokenCookieName?: string | null;
    defaultTenantGlobalId?: string | null;
  }) => api.post("/automation-connections", data).then((r) => r.data as import("./types").AutomationConnectionListItem),
  update: (id: number, data: {
    name: string;
    targetUrl?: string | null;
    authHeadersJson?: string | null;
    httpMethod?: string | null;
    connectionType?: "Static" | "BranchLogin" | "SharedSecret";
    username?: string | null;
    password?: string | null;
    loginPathTemplate?: string | null;
    tokenCookieName?: string | null;
    defaultTenantGlobalId?: string | null;
  }) => api.put(`/automation-connections/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/automation-connections/${id}`),
  regenerateSecret: (id: number) => api.post(`/automation-connections/${id}/regenerate-secret`).then((r) => r.data),
};

// SQLly in the browser, per branch — see BranchSqllyCard.
export const sqllyApi = {
  status: (branchId: number) => api.get(`/branches/${branchId}/sqlly`).then((r) => r.data as SqllyStatus),
  /** Writes the branch's profile on the bridge and mints a launch token for the current user. */
  launch: (branchId: number) => api.post(`/branches/${branchId}/sqlly/launch`).then((r) => r.data as SqllyLaunch),
  /** The desktop app's credential file for this branch, with passwords — served as a download. */
  desktopCredentialsUrl: (branchId: number) => `/api/branches/${branchId}/sqlly/credentials`,
  /** Administrators only, like everything below: SQLly Embedded on this server. */
  package: () => api.get("/sqlly/package").then((r) => r.data as SqllyPackage),
  /** Starts downloading the latest build from sqlly.app in the background; poll `package()`. */
  updateToLatest: () => api.post("/sqlly/package/update").then((r) => r.data as SqllyPackage),
  /** Writes every live branch's connections to the bridge now. */
  refreshBranches: () => api.post("/sqlly/package/refresh-branches").then((r) => r.data as SqllyRefreshResult),
  installPackage: (file: File, onProgress?: (fraction: number) => void) =>
    api
      .put("/sqlly/package", file, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (e) => e.total && onProgress?.(e.loaded / e.total),
      })
      .then((r) => r.data as SqllyPackage),
  uploadLicense: (file: File) =>
    api
      .put("/sqlly/package/license", file, { headers: { "Content-Type": "application/octet-stream" } })
      .then((r) => r.data as SqllyPackage),
};
